import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { Team, TeamCreate, TeamUpdate } from '@/app/types/team';
import { createTeam, updateTeam } from '@/app/services/teamService';
import { listUsers } from '@/app/services/userService';

interface TeamFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: Team | null;
  onSuccess?: () => void;
}

const TeamForm: React.FC<TeamFormProps> = ({ open, onOpenChange, team, onSuccess }) => {
  const isEditMode = !!team;
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [leadId, setLeadId] = useState<number | null>(null);
  const [memberIds, setMemberIds] = useState<number[]>([]);

  const { data: usersResponse } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => listUsers({ limit: 200 }),
    enabled: open,
  });
  const users = usersResponse?.items ?? [];

  useEffect(() => {
    if (open) {
      setName(team?.name ?? '');
      setDescription(team?.description ?? '');
      setIsActive(team?.is_active ?? true);
      setLeadId(team?.team_lead_user_id ?? null);
      setMemberIds(team?.members?.map((m) => m.id) ?? []);
    }
  }, [open, team]);

  const createMutation = useMutation({
    mutationFn: (data: TeamCreate) => createTeam(data),
    onSuccess: () => {
      toast.success('Team created');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to create team'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: TeamUpdate) => updateTeam(team!.id, data),
    onSuccess: () => {
      toast.success('Team updated');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to update team'),
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Team name is required');
      return;
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      team_lead_user_id: leadId,
      is_active: isActive,
      member_user_ids: memberIds,
    };
    if (isEditMode) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const toggleMember = (userId: number, checked: boolean) => {
    setMemberIds((prev) =>
      checked ? [...new Set([...prev, userId])] : prev.filter((id) => id !== userId)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Team' : 'Create Team'}</DialogTitle>
          <DialogDescription>
            A team groups staff so any member can pick up tasks assigned to the team.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Name</Label>
            <Input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. GST Team"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-desc">Description</Label>
            <Textarea
              id="team-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this team handles"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Team Lead (optional)</Label>
            <Select
              value={leadId?.toString() ?? 'none'}
              onValueChange={(v) => setLeadId(v === 'none' ? null : parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select lead" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No lead</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id.toString()}>
                    {u.full_name || u.username || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Members ({memberIds.length})</Label>
            <ScrollArea className="h-48 rounded-md border p-2">
              <div className="space-y-1">
                {users.length === 0 && (
                  <p className="text-sm text-muted-foreground px-1 py-2">No users found.</p>
                )}
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={memberIds.includes(Number(u.id))}
                      onCheckedChange={(c) => toggleMember(Number(u.id), c === true)}
                    />
                    <span className="text-sm">{u.full_name || u.username || u.email}</span>
                  </label>
                ))}
              </div>
            </ScrollArea>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="team-active">Active</Label>
              <p className="text-xs text-muted-foreground">Inactive teams are hidden from selectors</p>
            </div>
            <Switch id="team-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TeamForm;
